import React, { useState } from 'react';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const theme = createTheme({
  components: {
    MuiSelect: {
      styleOverrides: {
        select: {
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
  },
});

const StyledFormControl = styled(FormControl)(({ isDarkMode }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    backgroundColor: isDarkMode ? '#374151' : '#ffffff',
    '&:hover': {
      transform: 'scale(1.01)',
      boxShadow: isDarkMode 
        ? '0 10px 25px rgba(0, 0, 0, 0.3)' 
        : '0 10px 25px rgba(0, 0, 0, 0.1)',
    },
    '&.Mui-focused': {
      boxShadow: isDarkMode
        ? '0 0 0 3px rgba(59, 130, 246, 0.3)'
        : '0 0 0 3px rgba(59, 130, 246, 0.2)',
    },
    '& fieldset': {
      borderColor: isDarkMode ? '#4b5563' : '#e5e7eb',
      transition: 'border-color 0.3s ease',
    },
    '&:hover fieldset': {
      borderColor: isDarkMode ? '#6b7280' : '#d1d5db',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    color: isDarkMode ? '#9ca3af' : '#6b7280',
    '&.Mui-focused': {
      color: '#3b82f6',
    },
  },
  '& .MuiSelect-select': {
    color: isDarkMode ? '#ffffff' : '#1f2937',
    fontWeight: 500,
  },
  '& .MuiSvgIcon-root': {
    color: isDarkMode ? '#9ca3af' : '#6b7280',
    transition: 'transform 0.3s ease',
  },
  '& .MuiSelect-iconOpen': {
    transform: 'rotate(180deg)',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ isDarkMode, selected }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 16px',
  transition: 'all 0.2s ease',
  backgroundColor: selected
    ? isDarkMode
      ? 'rgba(59, 130, 246, 0.2)'
      : 'rgba(59, 130, 246, 0.1)'
    : 'transparent',
  color: selected
    ? '#3b82f6'
    : isDarkMode
    ? '#ffffff'
    : '#1f2937',
  fontWeight: selected ? 600 : 400,
  '&:hover': {
    backgroundColor: isDarkMode ? '#374151' : '#f3f4f6',
  },
  '&.Mui-selected': {
    backgroundColor: isDarkMode
      ? 'rgba(59, 130, 246, 0.2)'
      : 'rgba(59, 130, 246, 0.1)',
    '&:hover': {
      backgroundColor: isDarkMode
        ? 'rgba(59, 130, 246, 0.3)'
        : 'rgba(59, 130, 246, 0.15)',
    },
  },
}));

function LanguageDropdown({
  selectLabel,
  id,
  name,
  value,
  onChange,
  optionsList,
  margin,
  disabled,
  isDarkMode
}) {
  const selectedOption = optionsList.find(option => option.value === value);

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ margin: margin || '1px' }}>
        <StyledFormControl fullWidth size="medium" isDarkMode={isDarkMode}>
          <InputLabel id={`${id}-label`}>{selectLabel}</InputLabel>
          <Select
            labelId={`${id}-label`}
            name={name}
            id={id}
            value={value}
            label={selectLabel}
            onChange={onChange}
            disabled={disabled}
            IconComponent={KeyboardArrowDownIcon}
            renderValue={(selected) => {
              const selectedOpt = optionsList.find(option => option.value === selected);
              return selectedOpt ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{selectedOpt.name}</span>
                  {selectedOpt.nativeWord && (
                    <span style={{ 
                      color: isDarkMode ? '#9ca3af' : '#6b7280',
                      fontSize: '0.875rem'
                    }}>
                      {selectedOpt.nativeWord}
                    </span>
                  )}
                </Box>
              ) : "";
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: '12px',
                  marginTop: '8px',
                  backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                  boxShadow: isDarkMode
                    ? '0 20px 25px -5px rgba(0, 0, 0, 0.5)'
                    : '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                  maxHeight: '300px',
                  border: isDarkMode ? '1px solid #374151' : '1px solid #e5e7eb',
                  '& .MuiList-root': {
                    padding: '1px',
                  },
                },
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
            }}
          >
            {optionsList.map((obj) => (
              <StyledMenuItem 
                key={obj.value} 
                value={obj.value}
                isDarkMode={isDarkMode}
                selected={value === obj.value}
              >
                <span>{obj.name}</span>
                {value === obj.value && (
                  <CheckIcon sx={{ fontSize: '20px', color: '#3b82f6' }} />
                )}
              </StyledMenuItem>
            ))}
          </Select>
        </StyledFormControl>
      </Box>
    </ThemeProvider>
  );
}

// Demo Component
export default function App(props) {
  const [language, setLanguage] = useState('en');
  const [darkMode, setDarkMode] = useState(false);

  return (
    <Box
      sx={{
        // minHeight: '100vh',
        padding: '1px',
        background: darkMode
          ? 'linear-gradient(135deg, #1f2937 0%, #111827 50%, #1f2937 100%)'
          : 'linear-gradient(135deg, #dbeafe 0%, #ffffff 50%, #fae8ff 100%)',
        transition: 'background 0.3s ease',
      }}
    >
      <Box sx={{ maxWidth: '400px', margin: '0 auto' }}>
        {/* <Box sx={{ textAlign: 'center', marginBottom: '24px' }}>
          <h1
            style={{
              fontSize: '2rem',
              fontWeight: 700,
              color: darkMode ? '#ffffff' : '#1f2937',
              marginBottom: '8px',
            }}
          >
            Translate to
          </h1>
          <p
            style={{
              fontSize: '0.875rem',
              color: darkMode ? '#9ca3af' : '#6b7280',
            }}
          >
            Select your preferred language
          </p>
        </Box>

        <Box
          component="button"
          onClick={() => setDarkMode(!darkMode)}
          sx={{
            width: '100%',
            padding: '12px 16px',
            marginBottom: '24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            backgroundColor: darkMode ? '#374151' : '#e5e7eb',
            color: darkMode ? '#ffffff' : '#1f2937',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: darkMode ? '#4b5563' : '#d1d5db',
            },
          }}
        >
          Toggle {darkMode ? 'Light' : 'Dark'} Mode
        </Box> */}

        <LanguageDropdown
          selectLabel={props.selectLabel}
          id={props.id}
          name="language"
          value={props.value}
          onChange={props.onChange}
          optionsList={props.optionsList}
          isDarkMode={props.isDarkMode}
        />

        {/* <Box
          sx={{
            marginTop: '24px',
            padding: '16px',
            borderRadius: '12px',
            backgroundColor: darkMode ? '#1f2937' : '#ffffff',
            color: darkMode ? '#ffffff' : '#1f2937',
            boxShadow: darkMode
              ? '0 10px 15px rgba(0, 0, 0, 0.3)'
              : '0 10px 15px rgba(0, 0, 0, 0.1)',
          }}
        >
          <p style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: '4px' }}>
            Selected:
          </p>
          <p style={{ fontSize: '1.125rem' }}>
            {languages.find(l => l.value === language)?.name}
          </p>
        </Box> */}
      </Box>
    </Box>
  );
}